import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  listTemplates,
  createTemplate,
  deleteTemplate,
} from "../api/rotaTemplatesApi.js";
import { DEFAULT_TEMPLATES, toTemplateBlocks } from "../utils/rotaModel.js";

// The per-campus template library: built-in starters + saved customs.
// `saveTemplate` stores structure only (staff stripped by toTemplateBlocks).
// Each op degrades independently — a missing backend leaves just the built-ins.
export default function useRotaTemplates(campusName) {
  const [customs, setCustoms] = useState([]);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);

  const reload = useCallback(() => {
    if (!campusName) {
      setCustoms([]);
      return;
    }
    const myReq = ++reqId.current;
    setLoading(true);
    listTemplates(campusName)
      .then((list) => {
        if (myReq !== reqId.current) return;
        setCustoms(Array.isArray(list) ? list : []);
        setLoading(false);
      })
      .catch(() => {
        if (myReq !== reqId.current) return;
        setCustoms([]);
        setLoading(false);
      });
  }, [campusName]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Built-in starters: those with no `campus` show everywhere; a campus-scoped
  // one (e.g. Loretto's standard day) only shows for that campus.
  const templates = useMemo(() => {
    const builtins = DEFAULT_TEMPLATES.filter(
      (t) => !t.campus || String(t.campus).toLowerCase() === String(campusName || "").toLowerCase()
    );
    return [...builtins, ...customs];
  }, [customs, campusName]);

  const saveTemplate = useCallback(
    async (name, blocks) => {
      const created = await createTemplate({
        campus: campusName,
        name,
        blocks: toTemplateBlocks(blocks),
      });
      reload();
      return created;
    },
    [campusName, reload]
  );

  const removeTemplate = useCallback(
    async (id) => {
      await deleteTemplate(id);
      reload();
    },
    [reload]
  );

  return { templates, loading, saveTemplate, removeTemplate, reload };
}
